import React, { PureComponent } from 'react';
import track from 'react-tracking';
import PropTypes from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import { reduxForm, formValues } from 'redux-form';
import { Link } from 'react-router-dom';
import Field from '@ynap/field';
import { Button, SimpleAsyncButton } from '@ynap/button';
import { RadioButtonGroup, RadioButton } from '@ynap/radio-button';
import LoginFormSection from '../LoginFormSection/LoginFormSection';
import RegistrationFormSection from '../RegistrationFormSection/RegistrationFormSection';
import { onSubmitFail, submitAndCollateErrorsPromise } from '@ynap/form-utils';
import Notification from '@ynap/notification';
import validate from './validate';
import collateErrors from './collateErrors';
import {
    trackLoginSuccess,
    trackLoginError,
    trackRegistrationSuccess,
    trackRegistrationOptIn,
    trackRegistrationError,
    pageData,
    trackForgotPassword,
    trackLoginOptions,
} from '../analytics';
import queryString from 'query-string';

const bem = new BEMHelper('LoginAndRegistrationForm');

@track(({ config }) => ({
    category: {
        primaryCategory: 'sign in',
        subCategory: config?.head?.appName,
    },
}))
export class LoginAndRegistrationForm extends PureComponent {
    static propTypes = {
        form: PropTypes.string,
        login: PropTypes.func,
        register: PropTypes.func,
        handleSubmit: PropTypes.func,
        className: PropTypes.string,
        hasAccount: PropTypes.string,
        titles: PropTypes.arrayOf(PropTypes.string).isRequired,
        submitting: PropTypes.bool,
        error: PropTypes.string,
        enableTitles: PropTypes.bool,
        affiliate: PropTypes.bool,
        locale: PropTypes.shape({
            id: PropTypes.string.isRequired,
        }).isRequired,
        initialValues: PropTypes.object,
        gdprConsent: PropTypes.boolean,
        config: PropTypes.shape({
            appName: PropTypes.string,
            links: PropTypes.shape({
                termsAndConditions: PropTypes.string,
                privacyPolicy: PropTypes.string,
            }),
        }),
        messages: PropTypes.shape({
            loginAndRegistrationForm: PropTypes.shape({
                hasAccount: PropTypes.func.isRequired,
                noAccount: PropTypes.func.isRequired,
                forgotPassword: PropTypes.func.isRequired,
                forgotPasswordLinkTitle: PropTypes.func.isRequired,
                submit: PropTypes.func.isRequired,
            }).isRequired,
            registrationForm: PropTypes.shape({
                termsAndConditions: PropTypes.func.isRequired,
                termsAndConditionsLink: PropTypes.func.isRequired,
            }),
            errors: PropTypes.shape({
                global: PropTypes.func.isRequired,
            }).isRequired,
        }).isRequired,
        _reduxForm: PropTypes.object,
        optimizely: PropTypes.object,
        countries: PropTypes.object,
        fetchCountries: PropTypes.func,
        onRegisteredUserChanged: PropTypes.func,
        tracking: PropTypes.shape({
            trackEvent: PropTypes.func.isRequired
        })
    };

    constructor() {
        super();
        this.state = {
            maMarketingConsent: null,
            preferredLanguage: null,
        };
        this.submit = this.submit.bind(this);
        this.handleToggleMktConsent = this.handleToggleMktConsent.bind(this);
        this.proceedForgotPassword = this.proceedForgotPassword.bind(this);
        this.onRadioBtnChange = this.onRadioBtnChange.bind(this);
    }

    @track((props) =>
        pageData({
            name: `${props.config?.head?.appName} - sign in`,
            type: 'sign in',
        })(props),
    )
    componentDidMount() {
        const { countries, fetchCountries, locale, initialValues } = this.props;

        if (!countries && fetchCountries) {
            fetchCountries();
        }

        const maMarketingConsent = initialValues?.registration?.maMarketingConsent;
        this.setState({ maMarketingConsent });

        if (window && window.SF && window.SF.getUserSummary) {
            const redirect = queryString.parse(window.location.search)['redirect']?.replace(':', '%3A'); // Prevent javascript: and data: query injections

            window.SF.getUserSummary().then((user) => {
                if (user) {
                    const { userAuthenticationStatus } = user;

                    if (typeof userAuthenticationStatus == 'undefined' || userAuthenticationStatus.toLowerCase() === 'full') {
                        const isRelativePath = redirect && redirect.indexOf('://') === -1 && redirect.indexOf('//') === -1;
                        const newLocation = isRelativePath ? redirect : `/${locale.id}/account`;
                        window.location.assign(newLocation);
                    }
                }
            });
        }
    }

    componentDidUpdate(prevProps) {
        const { loginConsent, handleSubmit } = this.props;

        /* istanbul ignore else */
        if (loginConsent && !prevProps.loginConsent) {
            handleSubmit((values) => this.submit(values, 450))();
        }
    }

    @track((props, state, methodArgs, promiseArgs) => {
        const { hasAccount } = props;
        const err = promiseArgs[1];
        if (err) {
            const errorMessage = err?.errors?._error;
            if (hasAccount === 'true') return trackLoginError({ errorMessage });
            return trackRegistrationError({ errorMessage });
        }
    })
    submit(values, delay) {
        const {
            form,
            login,
            register,
            affiliate,
            optimizely,
            tracking,
            locale: { country, language },
            multipleLanguageCountries,
        } = this.props;

        // login path
        if (values.hasAccount === 'true') {
            const onSuccessfulLogin = () => {
                optimizely?.track('account_signinrate');
                tracking.trackEvent(trackLoginSuccess());
            }
            const action = login({ logonId: values.logonId, affiliate, ...values.login }, form, delay, undefined, onSuccessfulLogin);
            return submitAndCollateErrorsPromise(action, values, this.props);
        }

        // register user path
        const { maMarketingConsent: maMarketingConsentFromState } = this.state;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { maMarketingConsent, ...rest } = values.registration;

        const updatedRegistration = {
            ...rest,
            ...(maMarketingConsentFromState && { maMarketingConsent: maMarketingConsentFromState }),
        };

        const fallbackLangISO =
            multipleLanguageCountries && multipleLanguageCountries.hasOwnProperty(country)
                ? multipleLanguageCountries[country].filter((langISO) => langISO.includes(language))[0]
                : language;

        const selectedPreferredLanguage = (maMarketingConsentFromState === 'true' && this.state.preferredLanguage) || fallbackLangISO;

        const onSuccessfulRegistration = () => {
            optimizely?.track('account_registrationrate');
            optimizely?.track(maMarketingConsentFromState === 'true' ? 'registration_newsletterchecked' : 'registration_newsletterunchecked');
            tracking.trackEvent(trackRegistrationSuccess(maMarketingConsentFromState));
            tracking.trackEvent(trackRegistrationOptIn(maMarketingConsentFromState));
        }
        const action = register({ logonId: values.logonId, affiliate, ...updatedRegistration, preferredLanguage: selectedPreferredLanguage }, delay, undefined, onSuccessfulRegistration);
        return submitAndCollateErrorsPromise(action, values, this.props);
    }

    handleToggleMktConsent(maMarketingConsent, preferredLanguage) {
        this.setState({ ...(maMarketingConsent && { maMarketingConsent }), ...(preferredLanguage && { preferredLanguage }) });
    }

    @track(trackForgotPassword)
    proceedForgotPassword() { }

    @track((props, state, [event]) => trackLoginOptions(event.currentTarget.value === 'true'))
    onRadioBtnChange(e, hasAccount) {
        this.props.onRegisteredUserChanged?.(hasAccount === 'true');
    }

    render() {
        const {
            messages,
            locale,
            className,
            titles,
            form,
            handleSubmit,
            submitting,
            error,
            config,
            enableTitles,
            multipleLanguageCountries = {},
            _reduxForm,
            optimizely,
            gdprConsent,
            ageConsent,
            tcConsent,
        } = this.props;

        const hasAccount = this.props.hasAccount ?? this.props.initialValues?.hasAccount;

        const { language, country } = locale;
        const forgotPassword = messages.loginAndRegistrationForm.forgotPassword();

        const isMultipleLanguageCountry = multipleLanguageCountries && multipleLanguageCountries.hasOwnProperty(country);

        const links = config?.head?.links
            ? Object.entries(config.head.links).reduce((acc, [key, value]) => ({
                ...acc,
                [key]: value && value.replace('{language}', language).replace('{country}', country).toLowerCase(),
            })
                , {})
            : {};

        const { values } = _reduxForm ?? {};

        const onSubmit = (submitFunc) => {
            return (event) => {
                optimizely?.track('registration_signup');
                handleSubmit(submitFunc)?.(event);
            };
        };

        return (
            <form id={form} method="post" className={bem(null, null, className)} onSubmit={handleSubmit ? onSubmit(this.submit) : undefined}>
                {error ? <Notification className={bem('error')} message={error} type="error" /> : null}

                <Field className={bem('hasAccount', null)} component={RadioButtonGroup} name="hasAccount" onChange={this.onRadioBtnChange}>
                    <RadioButton value="true" label={messages.loginAndRegistrationForm.hasAccount()} />
                    <RadioButton value="false" label={messages.loginAndRegistrationForm.noAccount()} />
                </Field>

                {hasAccount === 'false' && isMultipleLanguageCountry | language !== 'en' ? (
                    <Notification className={bem('languagePrompt')} message={messages.loginAndRegistrationForm.languageRegistrationPrompt()} type="info" />
                ) : null}

                <Field className={bem('email')} name="logonId" type="email" label={messages.loginAndRegistrationForm.logonId()} autoComplete="username" />

                {hasAccount === 'true' ? (
                    <LoginFormSection name="login" messages={messages} hideLogonId optimizely={optimizely} />
                ) : (
                    <RegistrationFormSection
                        name="registration"
                        form={form}
                        messages={messages}
                        titles={titles}
                        links={links}
                        hideLogonId
                        enableTitles={enableTitles}
                        handleToggleMktConsent={this.handleToggleMktConsent}
                        supportedLanguages={multipleLanguageCountries[country]}
                        maMarketingConsent={this.state.maMarketingConsent}
                        preferredLanguage={this.state.preferredLanguage}
                        locale={locale}
                        enableGenderSelection={config?.head?.flags?.enableGenderSelection}
                        gdprConsent={gdprConsent}
                        enableAgeConsent={ageConsent}
                        enableTcConsent={tcConsent}
                        optimizely={optimizely}
                        formValues={values?.registration}
                        changeField={this.props.change}
                    />
                )}

                <SimpleAsyncButton
                    type="submit"
                    className={bem('submit')}
                    containerClass={bem('submitContainer')}
                    loading={submitting}
                    nohover
                    copy={messages.loginAndRegistrationForm.submit({ hasAccount })}
                />

                {hasAccount === 'true' ? (
                    <div className={bem('forgotPassword')}>
                        {forgotPassword ? `${forgotPassword} ` : null}
                        <Link to={`/${locale.id}/account/forgot-password`} className={bem('forgotPasswordLink')} onClick={this.proceedForgotPassword}>
                            {messages.loginAndRegistrationForm.forgotPasswordLinkTitle({ type: '' })}
                        </Link>
                    </div>
                ) : (
                    <p className={bem('terms')}>
                        {messages.registrationForm.termsAndConditions()}
                        <Button type="link" component="a" href={links.termsAndConditions} target="_blank" rel="noopener" className={bem('tandc')}>
                            {messages.registrationForm.termsAndConditionsLink()}
                        </Button>
                    </p>
                )}
            </form>
        );
    }
}

const LoginAndRegistrationFormWithValues = formValues({
    hasAccount: 'hasAccount',
    loginConsent: 'login.externalConsentsProvided',
})(LoginAndRegistrationForm);

export default reduxForm({ form: 'loginRegistration', validate, collateErrors, onSubmitFail })(LoginAndRegistrationFormWithValues);
